import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Loader2 } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    // Simulate send
    await new Promise(r => setTimeout(r, 1000));
    toast.success("Message sent! We'll get back to you shortly.");
    setForm({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="py-20 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-6 xl:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Left, info */}
            <div className="lg:col-span-5">
              <p className="font-manrope text-[13px] font-semibold tracking-[0.15em] uppercase text-paw-accent mb-4">Contact</p>
              <h1 className="font-outfit font-bold text-[clamp(2rem,4vw,3rem)] text-paw-text tracking-tight leading-[1.1] mb-6">Get in touch</h1>
              <p className="font-manrope text-[16px] text-paw-text-secondary leading-[1.75] mb-12">
                Have a question about your order, our process, or anything else? We'd love to hear from you. Fill out the form and we'll get back to you as soon as possible.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-paw-surface-alt flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="w-4 h-4 text-paw-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-[15px] text-paw-text mb-0.5">Email</p>
                    <p className="font-manrope text-[13px] text-paw-text-muted">info@pawlinx.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-paw-surface-alt flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-paw-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-[15px] text-paw-text mb-0.5">Partnerships</p>
                    <p className="font-manrope text-[13px] text-paw-text-muted">In partnership with Canadian pharmacies and veterinarians.</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-paw-surface-alt rounded-2xl">
                <p className="font-outfit font-semibold text-[15px] text-paw-text mb-1">Need a quick price?</p>
                <p className="font-manrope text-[13px] text-paw-text-muted mb-4">Get an instant estimate without filling out the form.</p>
                <button onClick={() => window.dispatchEvent(new Event("open-estimate-modal"))} className="bg-paw-accent hover:bg-paw-accent-hover text-white rounded-lg px-5 py-2 text-[13px] font-manrope font-semibold transition-all inline-flex items-center gap-1.5" data-testid="contact-order-cta">
                  Get Estimate <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Right, form */}
            <div className="lg:col-span-7 lg:pl-8">
              <form onSubmit={handleSubmit} className="bg-white border border-paw-border rounded-2xl p-8 md:p-10 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="font-manrope text-sm font-medium text-paw-text">Name</Label>
                    <Input required value={form.name} onChange={update("name")} placeholder="Your name" className="h-11 rounded-xl border-paw-border" data-testid="contact-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-manrope text-sm font-medium text-paw-text">Email</Label>
                    <Input type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className="h-11 rounded-xl border-paw-border" data-testid="contact-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-manrope text-sm font-medium text-paw-text">Subject</Label>
                  <Input required value={form.subject} onChange={update("subject")} placeholder="What's this about?" className="h-11 rounded-xl border-paw-border" data-testid="contact-subject" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-manrope text-sm font-medium text-paw-text">Message</Label>
                  <Textarea required value={form.message} onChange={update("message")} placeholder="Tell us more..." rows={6} className="rounded-xl border-paw-border resize-none" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={sending} className="bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-xl h-11 px-8 font-manrope font-semibold text-sm transition-colors flex items-center gap-2" data-testid="contact-submit-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
